<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  blockedByPlan: {
    type: Boolean,
    default: false,
  },
  availableInPlan: {
    type: Boolean,
    default: true,
  },
});

const router = useRouter();
const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const isBlocked = computed(() => props.blockedByPlan || !props.availableInPlan);

const integrationStatus = computed(() => {
  if (isBlocked.value) {
    return t('INTEGRATION_APPS.STATUS.BLOCKED_BY_PLAN');
  }
  return props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED');
});

const integrationStatusColor = computed(() => {
  if (isBlocked.value) {
    return 'bg-n-amber-9';
  }
  return props.enabled ? 'bg-n-teal-9' : 'bg-n-slate-8';
});

const statusIcon = computed(() => {
  if (isBlocked.value) {
    return 'i-lucide-lock';
  }
  return 'i-ph-check-bold';
});

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);

const goToBilling = () => {
  router.push({
    name: 'omni_billing_settings',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div
    class="flex flex-col flex-1 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow overflow-hidden"
    :class="{ 'opacity-75': isBlocked }"
  >
    <!-- Blocked Banner -->
    <div
      v-if="isBlocked"
      class="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-b border-amber-200 dark:border-amber-800 flex items-center justify-between gap-3"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="i-lucide-lock w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0"
        />
        <span
          class="text-xs font-medium text-amber-700 dark:text-amber-300 truncate"
        >
          {{ $t('INTEGRATION_APPS.BLOCKED_BY_PLAN') }}
        </span>
      </div>
      <button
        class="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-md transition-colors whitespace-nowrap"
        @click="goToBilling"
      >
        {{ $t('INTEGRATION_APPS.UPGRADE_PLAN') }}
      </button>
    </div>

    <!-- Card Content -->
    <div class="p-6 flex-1 flex flex-col">
      <div class="flex items-start justify-between">
        <div class="flex h-12 w-12 mb-4">
          <img
            :src="`/dashboard/images/integrations/${id}.png`"
            class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
            :class="{ 'grayscale opacity-50': isBlocked }"
          />
          <img
            :src="`/dashboard/images/integrations/${id}-dark.png`"
            class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
            :class="{ 'grayscale opacity-50': isBlocked }"
          />
        </div>
        <span
          v-tooltip="integrationStatus"
          class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
          :class="integrationStatusColor"
        >
          <i :class="[statusIcon, 'text-sm']" />
        </span>
      </div>
      <div class="flex flex-col m-0 flex-1">
        <div
          class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
        >
          <span class="text-base font-semibold">{{ name }}</span>
          <router-link v-if="!isBlocked" :to="actionURL">
            <Button :label="$t('INTEGRATION_APPS.CONFIGURE')" link />
          </router-link>
          <Button
            v-else
            :label="$t('INTEGRATION_APPS.UPGRADE_PLAN')"
            link
            @click="goToBilling"
          />
        </div>
        <p class="text-n-slate-11">
          {{ replaceInstallationName(description) }}
        </p>
      </div>
    </div>
  </div>
</template>
